import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { DataTable } from '@/components/data-table'
import { Notice, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ConfirmAction } from '@/components/confirm-action'
import { TrendingEditor } from '@/features/trending/trending-editor'
import {
  TRENDING_SORTS,
  useDeleteTrending,
  useSetTrendingStatus,
  useTrendingList,
  type TrendingRow,
  type TrendingSort,
} from '@/features/trending/api'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { localStamp } from '@/lib/datetime'

const PAGE_SIZE = 20

const LOCALE_LABEL: Record<string, string> = {
  en: 'EN',
  'zh-tw': '繁中',
  ko: '한',
  ja: '日',
}

export function TrendingPage() {
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<TrendingSort>('position')
  const [editing, setEditing] = useState<{ item?: TrendingRow } | null>(null)
  const [pendingDelete, setPendingDelete] = useState<TrendingRow | null>(null)

  // Sorting is the SERVER's, and every column below sets enableSorting: false.
  // DataTable registers getSortedRowModel() unconditionally and does not pass
  // manualSorting on its server-paged branch, so a sortable header here would
  // reorder the twenty rows on screen while the pager still read "Page 1 of 4".
  //
  // Status is deliberately NOT passed. The position clamp counts the table
  // unfiltered, so a filtered list would show badges 2, 5, 7, 11 while typing 3
  // meant none of them. Filtering needs the position input hidden first.
  const query = useTrendingList({ page, pageSize: PAGE_SIZE, sort })
  const setStatus = useSetTrendingStatus()
  const remove = useDeleteTrending()

  const columns = useMemo<ColumnDef<TrendingRow, unknown>[]>(
    () => [
      {
        accessorKey: 'position',
        header: 'Position',
        enableSorting: false,
        // Always a number now: position is a dense 1..N sequence, so there is no
        // "unpinned" case left to render as an em-dash.
        cell: ({ row }) => <Badge variant="accent">{row.original.position}</Badge>,
      },
      {
        accessorKey: 'trending_value',
        header: 'Value',
        enableSorting: false,
        cell: ({ row }) => <span className="tabular-nums">{row.original.trending_value}</span>,
      },
      {
        id: 'title',
        header: 'Title (EN)',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-2.5">
            <div className="size-8 shrink-0 overflow-hidden rounded bg-secondary">
              {row.original.cover_url && (
                <img src={row.original.cover_url} alt="" className="size-full object-cover" />
              )}
            </div>
            <span className="font-semibold">{row.original.locales.en?.title || '—'}</span>
          </div>
        ),
      },
      {
        id: 'link_url',
        header: 'Link',
        enableSorting: false,
        cell: ({ row }) => {
          const linkUrl = row.original.link_url
          if (!linkUrl) return <span className="text-muted-foreground">—</span>
          return (
            <a
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block max-w-[16ch] truncate text-sm text-accent-foreground underline underline-offset-2"
              title={linkUrl}
            >
              {linkUrl.replace(/^https?:\/\//, '')}
            </a>
          )
        },
      },
      {
        id: 'locales',
        header: 'Locales',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.filled_locales.map((code) => LOCALE_LABEL[code] ?? code).join(' · ')}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge variant={row.original.status === 'published' ? 'success' : 'default'}>
            {row.original.status === 'published' ? 'Published' : 'Draft'}
          </Badge>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        enableSorting: false,
        cell: ({ row }) => {
          const updatedAt = row.original.updated_at
          if (!updatedAt) return <span className="text-muted-foreground">—</span>
          const [date, time, zone] = localStamp(updatedAt).split(' ')
          return (
            <span className="whitespace-nowrap text-muted-foreground">
              {date}{' '}
              <span className="text-faint">
                {time} {zone}
              </span>
            </span>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const item = row.original
          const published = item.status === 'published'
          return (
            <div className="flex justify-end gap-2 whitespace-nowrap">
              <Button
                variant="secondary"
                size="sm"
                disabled={setStatus.isPending}
                onClick={() =>
                  setStatus.mutate({ id: item.id, status: published ? 'draft' : 'published' })
                }
              >
                {published ? 'Unpublish' : 'Publish'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setEditing({ item })}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setPendingDelete(item)}>
                Delete
              </Button>
            </div>
          )
        },
      },
    ],
    [setStatus],
  )

  return (
    <>
      <PageHeader
        title="Trending News"
        actions={
          <Button onClick={() => setEditing({})}>
            <Plus />
            New entry
          </Button>
        }
      />

      <Notice>
        <b>Curated feed</b>, shown as its own page on the consumer site alongside the
        auto-aggregated news. Titles support four locales — English required, the rest optional and
        falling back to English. <b>Manual publish only — no scheduling in Phase 1.</b>
        <br />
        <b>Position orders this table only.</b> New entries are added at the end, and editing a
        position swaps the two entries — the one already in that slot takes the edited entry&rsquo;s
        old place, and nothing else renumbers. The public feed is ordered by <b>trending value</b>,
        so reordering here does not change what readers see.
      </Notice>

      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Label htmlFor="trending-sort" className="text-xs text-muted-foreground">
            Sort
          </Label>
          <Select
            value={sort}
            onValueChange={(next) => {
              setSort(next as TrendingSort)
              // Page 1: page 3 of a position sort is a different set of rows from
              // page 3 of a value sort, so holding the number strands the operator.
              setPage(1)
            }}
          >
            <SelectTrigger id="trending-sort" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRENDING_SORTS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DataTable
          columns={columns}
          query={query}
          pagination={{ page, onPageChange: setPage }}
          emptyMessage="No trending entries yet."
        />
      </Card>

      {/* Keyed so switching rows rebuilds the form with that row's values. */}
      {editing && (
        <TrendingEditor
          key={editing.item?.id ?? 'new'}
          item={editing.item}
          open
          onOpenChange={(open) => !open && setEditing(null)}
          onSaved={(row) => {
            /*
             * Follow the entry to wherever the server put it.
             *
             * A new entry lands at MAX+1, so on any list longer than one page the
             * modal used to close onto an apparently unchanged table — no toast
             * (the SPA has none), no row, nothing. The one number an operator
             * cannot predict is the one that says where to look.
             *
             * Only under the position sort: page N of a value or recency sort has
             * no relationship to position, so jumping would land nowhere useful.
             */
            if (sort !== 'position') return
            setPage(Math.max(1, Math.ceil(row.position / PAGE_SIZE)))
          }}
        />
      )}

      <ConfirmAction
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete this entry?"
        description={
          <>
            “{pendingDelete?.locales.en?.title}” will be removed for every locale. This cannot be
            undone.
          </>
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => remove.mutateAsync(pendingDelete!.id)}
        onConflict={() => void query.refetch()}
      />
    </>
  )
}
